import type { Transaction } from "@rocicorp/zero";
import * as z from "zod";
import { type Context, userFacingError } from "../authorization";
import { type Schema, zql } from "../generatedDrizzleSchema";
import { findConflictedFiles } from "../commitFiles";
import { clientGeneratableIdSchema } from "../id";
import { creatorColumns } from "../rbac/roles";
import { CHANGE_REVIEW_TIMEOUT_MS } from "../publishedChangeReview";
import { snapshotCommitFiles } from "../stepTrees";
import { mutator } from "./mutator";

export const requestWorkflowChangeReviewSchema = z.object({
  id: clientGeneratableIdSchema,
  workflowId: z.string(),
  commitId: z.string(),
});

export const requestWorkflowChangeReview = mutator({
  schema: requestWorkflowChangeReviewSchema,
  description: "Reviews workflow changes in a draft or published version.",
  interactionType: "review",
  resolve: {
    workflow: { kind: "workflow", from: "workflowId" },
    commit: { kind: "commit", from: "commitId" },
  },
  permissions: [
    {
      permission: "space:write",
      scopeId: ({ args }) => args.workflow.spaceId,
    },
  ],
  fn: async ({ tx, ctx, args: { id, workflow, commit } }) => {
    if (commit.branch.workflowId !== workflow.id)
      throw userFacingError("Commit not found");
    const generating = await tx.run(
      zql.workflowChangeReviews
        .where("commitId", commit.id)
        .where("status", "generating")
        .where("createdAt", ">", Date.now() - CHANGE_REVIEW_TIMEOUT_MS)
        .one()
    );
    if (generating)
      throw userFacingError("3B is already reviewing these changes.");
    let baseCommitId = commit.parentCommitId;
    if (commit.branch.name !== "main") {
      await tx.mutate.workflowBranches.update({
        id: commit.branch.id,
        lastChatEntryAt: Date.now(),
      });
      const activeChat = await tx.run(
        zql.chats
          .where("respondingAt", "IS NOT", null)
          .whereExists("commit", (c) => c.where("branchId", commit.branch.id))
          .one()
      );
      if (activeChat)
        throw userFacingError(
          "Wait for 3B to finish before reviewing changes."
        );
      const main = await tx.run(
        zql.workflowBranches
          .where("workflowId", workflow.id)
          .where("name", "main")
          .one()
      );
      baseCommitId = main?.headCommitId ?? null;
      if (!commit.message) {
        if ((await findConflictedFiles({ tx, commitId: commit.id })).length)
          throw userFacingError(
            "Resolve merge conflicts before reviewing changes."
          );
        // A checkpoint keeps later edits from changing the reviewed files.
        await snapshotCommitFiles({
          tx,
          commitId: commit.id,
          tenantId: ctx.tenantId,
        });
        await tx.mutate.workflowCommits.update({
          id: commit.id,
          message: "Change review",
        });
      }
    }
    await insertWorkflowChangeReview(tx, ctx, id, commit, baseCommitId);
  },
});

export async function insertWorkflowChangeReview(
  tx: Transaction<Schema>,
  ctx: Context,
  id: string,
  commit: { id: string; parentCommitId: string | null },
  baseCommitId = commit.parentCommitId
) {
  await tx.mutate.workflowChangeReviews.insert({
    id,
    tenantId: ctx.tenantId,
    commitId: commit.id,
    baseCommitId,
    createdAt: Date.now(),
    ...creatorColumns(ctx),
    status: "generating",
    summary: "",
    changes: [],
  });
}
